<script>
export default {
  props: {
    bannerMessage: {
      type: String,
      default: '',
    },
    hrefLink: {
      type: String,
      default: '',
    },
    hrefLinkText: {
      type: String,
      default: '',
    },
    hasActionButton: {
      type: Boolean,
      default: false,
    },
    actionButtonVariant: {
      type: String,
      default: '',
    },
    actionButtonLabel: {
      type: String,
      default: '',
    },
    actionButtonIcon: {
      type: String,
      default: 'arrow-right',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    hasCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    bannerClasses() {
      const classList = [this.colorScheme];

      if (this.hasActionButton || this.hasCloseButton) {
        classList.push('has-button');
      }
      return classList;
    },
    // Um icone a esquerda diz o tipo do aviso antes de a pessoa ler a frase.
    // Nomes-base de proposito: o FluentIcon concatena o `type` (`-outline`),
    // entao passar `info-outline` aqui procuraria `info-outline-outline` e
    // estouraria -- foi assim que ContactIntro e ReminderItem quebraram.
    bannerIcon() {
      return (
        {
          primary: 'info',
          secondary: 'info',
          alert: 'error-circle',
          warning: 'warning',
          gray: 'info',
        }[this.colorScheme] || 'info'
      );
    },
  },
  methods: {
    onClick(e) {
      this.$emit('click', e);
    },
    onClickClose(e) {
      this.$emit('close', e);
    },
  },
};
</script>

<template>
  <!-- `justify-between` e nao `justify-center`: texto centralizado com botao na
       ponta e leitura de barra de sistema antiga. E `min-h` em vez de `h-12`
       fixo, senao mensagem longa fica cortada.
       `flex-wrap`: mensagem longa mais botao de acao nao cabem numa linha em
       tela estreita, e sem quebrar o botao era comprimido ate o rotulo virar
       reticencias. Agora as acoes descem, continuando alinhadas a direita. -->
  <div
    class="flex flex-wrap items-center justify-between min-h-[3rem] gap-x-4 gap-y-2 px-4 py-2.5 text-sm shrink-0 banner border-b"
    :class="bannerClasses"
  >
    <span class="banner-message">
      <fluent-icon :icon="bannerIcon" size="16" class="banner-icon shrink-0" />
      {{ bannerMessage }}
      <a
        v-if="hrefLink"
        :href="hrefLink"
        rel="noopener noreferrer nofollow"
        target="_blank"
      >
        {{ hrefLinkText }}
      </a>
    </span>
    <div class="actions">
      <woot-button
        v-if="hasActionButton"
        size="tiny"
        :icon="actionButtonIcon"
        :variant="actionButtonVariant"
        color-scheme="primary"
        class-names="banner-action__button"
        @click="onClick"
      >
        {{ actionButtonLabel }}
      </woot-button>
      <!-- `clear`: com o fundo agora tingido, um botao solido de dispensar
           pesaria mais que a propria mensagem. -->
      <woot-button
        v-if="hasCloseButton"
        size="tiny"
        variant="clear"
        :color-scheme="colorScheme"
        icon="dismiss-circle"
        class-names="banner-action__button"
        @click="onClickClose"
      >
        {{ $t('GENERAL_SETTINGS.DISMISS') }}
      </woot-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Aviso com fundo tingido, nao barra saturada.
//
// O visual anterior era o alerta cheio do Bootstrap de 2015: `bg-woot-500`,
// `bg-red-500`, `bg-yellow-400` ocupando a largura inteira com texto branco
// centralizado. Tres problemas concretos, alem da idade:
//
//   - o esquema `secondary` usava `bg-slate-200 dark:bg-slate-300`, ou seja,
//     uma barra CLARA no tema escuro.
//   - `warning` era #FFD700 puro com texto quase preto; brigava com tudo.
//   - `gray` pintava `text-black-500` sem definir fundo algum.
//
// Agora cada esquema usa fundo -50 (ou -900/40 no escuro) com texto -700 e uma
// borda inferior que separa do conteudo. O icone herda a cor do texto.
.banner {
  &.primary {
    @apply bg-brand-subtle dark:bg-brand-subtle text-brand-text dark:text-brand-text
      border-flipdesk-100 dark:border-flipdesk-800;
  }

  &.secondary,
  &.gray {
    @apply bg-surface-sunken dark:bg-surface-sunken text-muted dark:text-muted
      border-line dark:border-line;
  }

  &.alert {
    @apply bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300
      border-red-100 dark:border-red-800;
  }

  &.warning {
    @apply bg-amber-50 text-amber-800
      border-amber-100;
  }

  a {
    @apply ml-1 underline text-current text-sm font-medium;
  }

  .banner-icon {
    @apply text-current opacity-80;
  }

  .banner-action__button {
    ::v-deep .button__content {
      @apply whitespace-nowrap;
    }
  }

  // `flex-1` com `basis-full sm:basis-auto`: em tela estreita a mensagem ocupa
  // a linha inteira e empurra as acoes para baixo; a partir de sm elas voltam
  // a dividir a mesma linha.
  .banner-message {
    @apply flex items-center gap-2 min-w-0 flex-1 basis-full sm:basis-auto;
  }

  .actions {
    @apply flex gap-1.5 shrink-0 ltr:ml-auto rtl:mr-auto;
  }
}
</style>
